import { Link } from 'react-router';
import { ScreenPlate } from './ScreenPlate';
import { EDGES } from '../theme/tokens';

// Shared chrome for the two browse-everything screens (/heroes, /minions).
//
// They are the same screen with different contents — a titled, filtered,
// scrolling catalogue — so the header, the back link, the filter row and the
// scroll container live here once. What differs is only what gets rendered
// into the grid, which is the child.
//
// Both are reachable from the main menu and neither is part of a match, so
// nothing here touches match state.

export interface GalleryFilter {
  id: string;
  label: string;
  /** Shown after the label, e.g. a count. */
  hint?: string;
}

export function GalleryShell({
  eyebrow,
  title,
  blurb,
  filters,
  activeFilter,
  onFilter,
  children,
}: {
  eyebrow: string;
  title: string;
  /** One line of "what am I looking at" — this is an explainer, not a list. */
  blurb: string;
  filters: GalleryFilter[];
  activeFilter: string;
  onFilter: (id: string) => void;
  children: React.ReactNode;
}) {
  return (
    <main className="pt-safe relative h-dvh w-full overflow-hidden bg-asphalt">
      <ScreenPlate src="plates/lineup_wall.webp" scrim={0.85} position="center 40%" />

      <section className="relative mx-auto flex h-full w-full max-w-6xl flex-col gap-sm px-md py-sm md:gap-md md:px-lg md:py-md lg:px-xl">
        <header className="flex shrink-0 items-start justify-between gap-sm">
          <div className="min-w-0">
            <p className="font-data text-[11px] uppercase tracking-[0.3em] text-marker md:text-[12px]">
              {eyebrow}
            </p>
            <h1 className="font-cholo text-[26px] leading-none text-sodium md:text-[38px] lg:text-[48px]">
              {title}
            </h1>
            <p className="mt-xs max-w-2xl text-[12px] leading-snug text-marker md:text-[14px]">
              {blurb}
            </p>
          </div>
          <Link
            to="/menu"
            aria-label="Back to menu"
            className="flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-sm border border-stucco3 bg-stucco2 font-data text-[16px] text-graphite hover:text-marker"
          >
            ←
          </Link>
        </header>

        {/* Filter row. Horizontally scrollable rather than wrapping: on a
            390px screen a wrapping row of nine chips eats three lines of the
            viewport before any content shows. */}
        <div
          className="-mx-md shrink-0 overflow-x-auto px-md md:mx-0 md:px-0"
          role="group"
          aria-label={`Filter ${title}`}
        >
          <div className="flex w-max gap-xs">
            {filters.map((f) => {
              const on = f.id === activeFilter;
              return (
                <button
                  key={f.id}
                  onClick={() => onFilter(f.id)}
                  aria-pressed={on}
                  className={`flex min-h-[44px] shrink-0 items-center gap-xs rounded-sm border px-md font-data text-[12px] uppercase tracking-[0.15em] transition-colors ${
                    on
                      ? 'border-sodium bg-sodium/20 text-sodium'
                      : 'bg-stucco1 text-graphite hover:bg-stucco2'
                  }`}
                  style={{ borderColor: on ? '#ff8a3d' : EDGES.faint }}
                >
                  {f.label}
                  {f.hint && (
                    <span className={on ? 'text-sodium/70' : 'text-graphite/70'}>
                      {f.hint}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* The catalogue itself owns the scroll, so the header and filters
            stay put while you browse. */}
        <div className="min-h-0 flex-1 overflow-y-auto pb-lg">{children}</div>
      </section>
    </main>
  );
}

/** Section heading used inside a gallery — a tribe, a tier, a faction. */
export function GallerySection({
  label,
  sublabel,
  accent,
  children,
}: {
  label: string;
  sublabel?: string;
  accent?: string;
  children: React.ReactNode;
}) {
  return (
    // content-visibility lets the browser skip layout and paint for groups
    // scrolled out of view. The minion catalogue is 150+ procedurally drawn
    // cards and this is the difference between a smooth scroll and a stutter
    // on the cheap Android this game targets.
    <section className="mb-lg" style={{ contentVisibility: 'auto' }}>
      <div className="mb-xs flex items-baseline gap-sm">
        <h2
          className="font-data text-[12px] uppercase tracking-[0.3em] md:text-[13px]"
          style={{ color: accent ?? '#c8b89a' }}
        >
          {label}
        </h2>
        {sublabel && (
          <p className="min-w-0 flex-1 truncate text-[11px] text-graphite md:text-[12px]">
            {sublabel}
          </p>
        )}
      </div>
      {children}
    </section>
  );
}
